import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, InputNumber, Switch, Button, Space, Divider, App } from 'antd';
import { MailOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { emailConfigAPI } from '../services/api';

const EmailConfig = () => {
  const { message } = App.useApp();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchConfig = useCallback(async () => {
    try {
      const response = await emailConfigAPI.get();
      if (response.success && response.data) {
        setConfig(response.data);
        form.setFieldsValue({
          smtp_host: response.data.smtp_host,
          smtp_port: response.data.smtp_port,
          smtp_secure: response.data.smtp_secure,
          smtp_user: response.data.smtp_user,
          from_email: response.data.from_email,
          from_name: response.data.from_name,
        });
      }
    } catch (error) {
      // No config found is okay for first time setup
    }
  }, [form]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveConfig = async (values) => {
    setLoading(true);
    try {
      // For security, don't send password if it's not changed
      const submitValues = { ...values };
      if (!submitValues.smtp_pass && config) {
        delete submitValues.smtp_pass;
      }

      if (config) {
        await emailConfigAPI.update(config.id, submitValues);
        message.success('Email configuration updated successfully');
      } else {
        await emailConfigAPI.create(submitValues);
        message.success('Email configuration saved successfully');
      }
      fetchConfig();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConfig = async () => {
    try {
      const values = await form.validateFields(['smtp_host', 'smtp_port', 'smtp_secure', 'smtp_user', 'smtp_pass', 'from_email']);
      setTestLoading(true);

      // Save config first if not saved
      if (!config) {
        await emailConfigAPI.create(values);
        setConfig(values);
      }

      // Get test email
      const testEmail = values.smtp_user;
      await emailConfigAPI.test(testEmail);
      message.success('Test email sent successfully!');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <MailOutlined />
          <span>Email Configuration</span>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveConfig}
        initialValues={{
          smtp_port: 587,
          smtp_secure: false,
          from_name: 'PingPulse Monitor'
        }}
      >
        <Form.Item
          label="SMTP Host"
          name="smtp_host"
          rules={[{ required: true, message: 'Please input SMTP host!' }]}
        >
          <Input placeholder="smtp.gmail.com" />
        </Form.Item>

        <Form.Item
          label="SMTP Port"
          name="smtp_port"
          rules={[{ required: true, message: 'Please input SMTP port!' }]}
        >
          <InputNumber min={1} max={65535} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="SMTP Secure (SSL/TLS)"
          name="smtp_secure"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="SMTP Username"
          name="smtp_user"
          rules={[{ required: true, message: 'Please input SMTP username!' }]}
        >
          <Input placeholder="your-email@gmail.com" />
        </Form.Item>

        <Form.Item
          label="SMTP Password"
          name="smtp_pass"
          rules={config ? [] : [{ required: true, message: 'Please input SMTP password!' }]}
        >
          <Input.Password placeholder={config ? '••••••••' : 'Your SMTP Password'} />
        </Form.Item>

        <Divider />

        <Form.Item
          label="From Email"
          name="from_email"
          rules={[
            { required: true, message: 'Please input from email!' },
            { type: 'email', message: 'Please enter a valid email!' }
          ]}
        >
          <Input placeholder="noreply@yourdomain.com" />
        </Form.Item>

        <Form.Item
          label="From Name"
          name="from_name"
        >
          <Input placeholder="PingPulse Monitor" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
            >
              Save Configuration
            </Button>
            <Button
              icon={<SendOutlined />}
              onClick={handleTestConfig}
              loading={testLoading}
            >
              Test Email
            </Button>
          </Space>
        </Form.Item>

        <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
          <h4>Notes:</h4>
          <ul>
            <li>For Gmail: Enable 2-factor authentication and use an App Password</li>
            <li>For Outlook/Hotmail: Your password is the same as your account password</li>
            <li>Port 587 is typically used with STARTTLS</li>
            <li>Port 465 is typically used with SSL/TLS</li>
            <li>Test email will be sent to the SMTP username address</li>
          </ul>
        </div>
      </Form>
    </Card>
  );
};

export default EmailConfig;
